import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import ProductVial from "./ProductVial";

/**
 * Cross-sell / upsell strip.
 * - Excludes current SKU and Bacteriostatic Water (that has its own bundle prompt).
 * - Prefers same-category, then featured, then in-stock.
 * - Defaults to 4 cards.
 */
export default function RecommendedProducts({
  currentSku,
  category,
  excludeSkus = [],
  limit = 4,
  title = "You may also like",
  subtitle = "Other research peptides purchased by UK laboratories",
  testid = "recommended-products",
}) {
  const [products, setProducts] = useState([]);
  const skipSkus = new Set([currentSku, "PL-BACT-3", ...excludeSkus].filter(Boolean));

  useEffect(() => {
    api.get("/products").then((r) => {
      const pool = (r.data || []).filter((p) => !skipSkus.has(p.sku));
      const score = (p) =>
        (p.category === category ? 3 : 0) +
        (p.featured ? 2 : 0) +
        (p.stock_status !== "out_of_stock" ? 1 : 0);
      pool.sort((a, b) => score(b) - score(a));
      setProducts(pool.slice(0, limit));
    }).catch(() => setProducts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSku, category, limit]);

  if (products.length === 0) return null;

  return (
    <section className="mt-14" data-testid={testid}>
      <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="pg-micro mb-2">// Keep stocking up</div>
          <h2 className="text-[#F7FAFC] pg-display text-3xl" style={{ fontFamily: "'Exo 2'" }}>
            {title}
          </h2>
          {subtitle && <p className="text-[#9CA8B8] text-sm mt-1">{subtitle}</p>}
        </div>
        <Link
          to="/products"
          className="pg-btn-ghost text-xs"
          data-testid={`${testid}-view-all`}
        >
          View full catalogue <ArrowRight size={12} />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {products.map((p) => {
          const inStock = p.stock_status !== "out_of_stock" && p.stock_quantity > 0;
          return (
            <Link
              key={p.sku}
              to={`/products/${p.sku}`}
              className="pg-card p-4 group hover:border-[#00C2D6]/50 transition-colors"
              data-testid={`${testid}-card-${p.sku}`}
            >
              <div className="aspect-square relative overflow-hidden border border-[#00C2D6]/10 rounded-sm mb-3">
                <ProductVial product={p} size="sm" />
                <div className="absolute top-2 left-2">
                  {inStock ? (
                    <span className="pg-badge-stock text-[9px]">
                      <span className="w-1 h-1 rounded-full bg-[#A6FF00]" /> In Stock
                    </span>
                  ) : (
                    <span className="pg-badge-out text-[9px]">OOS</span>
                  )}
                </div>
              </div>
              <div className="text-[#F7FAFC] pg-heading text-sm leading-tight mb-1" style={{ fontFamily: "'Rajdhani'" }}>
                {p.name}
              </div>
              <div className="pg-mono text-[10px] text-[#9CA8B8] uppercase tracking-[0.18em] mb-2">
                {p.strength} · {p.purity || "≥99%"}
              </div>
              <div className="flex items-baseline justify-between">
                <div className="flex items-baseline gap-2">
                  <span className="text-[#F7FAFC] pg-display text-lg" style={{ fontFamily: "'Exo 2'" }}>
                    £{Number(p.price).toFixed(2)}
                  </span>
                  {p.compare_at_price && p.on_sale && (
                    <span className="text-[#9CA8B8] line-through pg-mono text-xs">
                      £{Number(p.compare_at_price).toFixed(2)}
                    </span>
                  )}
                </div>
                <ShieldCheck size={12} className="text-[#5EEAD4]" />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
